import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import React, { useCallback, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Modal,
  StyleSheet,
  View,
} from "react-native";
import { format, addWeeks, subWeeks, startOfWeek, endOfWeek } from "date-fns";
import Text from "@components/CustomText";
import { useFocusEffect } from "@react-navigation/native";
import NoDeliveryPerf from "@assets/svg/NoDeliveryPerf";
import {
  getDailyPerformance,
  getWeekDayPerformance,
  getWeeklyPerformance,
  getPlanModalData,
} from "@services/api";
import color from "@constants/color";
import analytics from "@services/analytics";
import t from "@constants/localization";
import { DELIVERY_PERFORMANCE } from "@constants/analyticsEventsCT";
import CpDbHOC from "@components/HOC/CpDbHOC";
import DeliveryPerformanceModal from "./components/DeliveryPerformanceModal";
import DaySelector from "./components/DaySelector";
import WeekSelector from "./components/WeekSelector";
import DeliveryPerformanceCard from "./components/DeliveryPerformanceCard";
import DeliveryPerformanceTabBtn from "./components/DeliveryPerfTabBtn";
import PlanModal from "./components/PlanModal";

const tabEnum = {
  TODAY: "today",
  WEEK: "week",
};

function DeliveryPerformance({ navigation, route, isCpDb }) {
  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState({});
  const [selectedTab, setSelectedTab] = useState(tabEnum.TODAY);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [showOverall, setShowOverall] = useState(true);
  const [weekChange, setWeekChange] = useState(false);
  const [planModalData, setPlanModalData] = useState({
    type: "",
    visible: false,
    loading: false,
    data: { title: "", type: "", lines: [] },
  });

  const PATH = "screens/DeliveryPerformance";

  const getCurrentWeekRange = (date) => {
    const start = startOfWeek(date, { weekStartsOn: 0 });
    const end = endOfWeek(date, { weekStartsOn: 0 });
    return {
      startDate: format(start, "dd MMMM"),
      endDate: format(end, "dd MMMM"),
      start,
      end,
    };
  };

  useFocusEffect(
    useCallback(() => {
      fetchData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab, currentDate, showOverall]),
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      if (selectedTab === tabEnum.TODAY) {
        const { data } = await getDailyPerformance(
          format(currentDate, "yyyy-MM-dd"),
        );
        setListData(data);
      } else {
        if (!showOverall) {
          const { data } = await getWeekDayPerformance(
            format(currentDate, "yyyy-MM-dd"),
            format(weekRange.start, "yyyy-MM-dd"),
            format(weekRange.end, "yyyy-MM-dd"),
          );
          setListData(data);
        } else {
          const { data } = await getWeeklyPerformance(
            format(weekRange.start, "yyyy-MM-dd"),
            format(weekRange.end, "yyyy-MM-dd"),
          );
          setListData(data);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousWeek = () => {
    setWeekChange(true);
    const newDate = subWeeks(currentDate, 1);
    setShowOverall(true);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    setWeekChange(true);
    const newDate = addWeeks(currentDate, 1);
    setShowOverall(true);
    setCurrentDate(newDate);
  };

  const weekRange = getCurrentWeekRange(currentDate);

  const keyExtractor = (item) => item?.key || item?.id;

  const handleShowModal = (data) => {
    setModalData(data);
    setShowModal(true);
    analytics.trackEvent(
      DELIVERY_PERFORMANCE.DELIVERY_PERFORMANCE_MODAL_CLICKED,
      {
        source: "Delivery Performance",
      },
    );
  };

  const handleShowPlanModal = async (type, cta_api) => {
    setPlanModalData((p) => ({ ...p, type, loading: true, visible: true }));
    try {
      const { data } = await getPlanModalData(cta_api);
      setPlanModalData((p) => ({ ...p, data }));
    } catch (error) {
      console.log(error);
      setPlanModalData((p) => ({ ...p, visible: false }));
    } finally {
      setPlanModalData((p) => ({ ...p, loading: false }));
    }
  };

  const handleSelectedTab = (tab) => {
    setCurrentDate(new Date());
    setShowOverall(true);
    setSelectedTab(tab);
  };

  const handleWeekSeletedDate = (date) => {
    setWeekChange(false);
    setCurrentDate(date);
    setShowOverall(false);
  };
  const handleShowOverall = () => {
    setShowOverall(true);
  };

  const renderItem = ({ item }) => {
    if (item?.key === "Tab") {
      return (
        <DeliveryPerformanceTabBtn
          selectedTab={selectedTab}
          setSelectedTab={handleSelectedTab}
        />
      );
    }
    if (item?.key === "WeekSelector") {
      return selectedTab === tabEnum.WEEK ? (
        <View>
          <WeekSelector
            startDate={weekRange.startDate}
            weekendDate={weekRange.end}
            endDate={weekRange.endDate}
            onPreviousWeek={handlePreviousWeek}
            onNextWeek={handleNextWeek}
            selectedDay={currentDate}
          />
          <DaySelector
            setCurrentDate={handleWeekSeletedDate}
            weekChange={weekChange}
            currentDate={currentDate}
            showOverall={showOverall}
            setShowOverall={handleShowOverall}
          />
        </View>
      ) : null;
    }
    if (item?.key === "Summary") {
      return renderHeader();
    }
    if (item?.key === "Empty") {
      return (
        <View style={styles.noDeliveriesContainer}>
          <NoDeliveryPerf />
          <Text
            variant="body_text_4"
            style={{ color: color.mediumBlacklightBlack, marginTop: 16 }}
          >
            {t(`${PATH}/noDeliveries`)}
          </Text>
        </View>
      );
    } else {
      return (
        <DeliveryPerformanceCard
          item={item}
          showModal={handleShowModal}
          setModalType={handleShowPlanModal}
          selectedTab={selectedTab}
        />
      );
    }
  };
  const _headerData = [
    {
      key: "Tab",
    },
    {
      key: "WeekSelector",
    },
    {
      key: "Summary",
    },
  ];

  const renderHeader = () => {
    if (isCpDb || !listData?.leader_report) return null;
    return (
      <View>
        <DeliveryPerformanceCard
          item={listData?.leader_report}
          showModal={handleShowModal}
          isHeader={true}
          setModalType={handleShowPlanModal}
        />
        {listData?.db_reports && listData?.db_reports.length ? (
          <View style={styles.headerDivider}>
            <Text
              newfont={true}
              variant="body_text_1"
              style={styles.headerText}
            >
              {t(`${PATH}/deliveryBoyReport`)}
            </Text>
            <View style={styles.dividerLine} />
          </View>
        ) : null}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle="Delivery Performance"
        headerColor="#fff"
        hideShadow={true}
        navigation={navigation}
        route={route}
      />
      <FlatList
        data={_headerData.concat(
          listData?.db_reports || [
            {
              key: "Empty",
            },
          ],
        )}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        showsVerticalScrollIndicator={false}
        stickyHeaderIndices={[0]}
      />
      <Modal
        statusBarTranslucent
        navigationBarTranslucent
        visible={loading}
        transparent={true}
      >
        <View
          style={[
            styles.noDeliveriesContainer,
            { backgroundColor: color.lightMediumBlack },
          ]}
        >
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      </Modal>
      <DeliveryPerformanceModal
        showModal={showModal}
        setShowModal={setShowModal}
        summary={modalData}
      />
      <PlanModal
        visible={planModalData?.visible}
        onClose={() => setPlanModalData((p) => ({ ...p, visible: false }))}
        data={planModalData?.data}
        loading={planModalData?.loading}
      />
    </View>
  );
}

export default CpDbHOC(DeliveryPerformance);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerDivider: {
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: 16,
  },
  headerText: {
    marginVertical: 8,
    marginRight: 10,
    color: color.black06,
  },
  dividerLine: {
    flex: 1,
    height: 0.5,
    backgroundColor: color.lightMediumBlack,
  },
  noDeliveriesContainer: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    height: 400,
  },
});
